import { Branch } from "./branch";
import { SingleSelectionMode } from "./single-selection-mode";
import { MultiTreeItemSubselect } from "./multi-tree-item-subselect";
import { SingleSubselect, TreeSelectDelegate, TreeSelection } from "./tree-select";
import { AbstractTreeSelectController } from "./tree-select-controller";
import http from "@/http";

export class SingleTreeSelectController extends AbstractTreeSelectController implements TreeSelectDelegate<SingleSubselect> {

    private treeSelect: SingleSelectionMode|null = null;

    getValue(): string[] {
        if (!this.treeSelect) {
            return [];
        }

        return this.treeSelect.value.values;
    }

    setValue(value: string[]): void {
        if (!this.treeSelect) {
            return;
        }
        this.treeSelect.value = Branch.fromValues(value);
    }

    connect(): void {
        this.initializeHydrator();
        this.settings = { ...this.settings, multiple: false };

        this.hydrator?.hydrateBranch(this.element).then(branch => {
            const root = branch[0];
            if (!root || root instanceof MultiTreeItemSubselect) {
                throw new Error('Single tree select requires a non-multi root subselect');
            }

            const singleBranch = branch.filter((subselect): subselect is SingleSubselect => !(subselect instanceof MultiTreeItemSubselect));
            if (singleBranch.length !== branch.length) {
                throw new Error('Single tree select branch cannot include multi subselects');
            }

            this.treeSelect = new SingleSelectionMode(this.element, this, root, singleBranch);
            this.treeSelect.connect();
            super.connect();
        });
    }

    disconnect(): void {
        this.treeSelect?.disconnect();
        this.treeSelect = null;
        super.disconnect();
    }

    getSubselect(context: TreeSelection<SingleSubselect>): Promise<SingleSubselect|null> {
        return this.loadSubselect(context, (subselect): subselect is SingleSubselect => {
            return !(subselect instanceof MultiTreeItemSubselect);
        });
    }

    getSubselects(root: SingleSubselect, value: Branch, onSubselectPlacement: (subselect: SingleSubselect) => void): void {
        const formData = this.createFormData(value.values, 'subselects');

        this.query(formData, 'Failed to load items')
            .then(http.decodeJson)
            .then((responses: string[]) => {
                let parent: SingleSubselect = root;
                let path = new Branch();

                responses.forEach((subselectHtml: string) => {
                    const selectedValue = value.values[path.level];
                    if (!selectedValue) {
                        return;
                    }

                    path = path.with(selectedValue);
                    const selection = new TreeSelection(this.element, path, parent);
                    const subselect = this.hydrator?.hydrateSubselect(selection, subselectHtml) ?? null;
                    if (!subselect || subselect instanceof MultiTreeItemSubselect) {
                        return;
                    }

                    onSubselectPlacement(subselect);
                    parent = subselect;
                });
            });
}
}
